import {VuexModule, Action, Mutation, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {PModelMeasurementSelection, PMSettings} from '@/views/administration/users/interfaces';
import {LayoutCard} from '@v/dashboard/interfaces';
import {MeasurementType} from '@/views/system/measurements/enums';
import {httpClient} from '@/api/baseApi';
import {SensorType} from '@v/dashboard/enums';
import {SystemNode} from '@/components/system/interfaces';
import userProfileModule from '@/store/modules/userProfileModule';
import {PModuleType} from '@e/configs';

const moduleName = 'userSettingsModule';

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class UserSettingsModule extends VuexModule {
  openNodes = localStorage.getItem('openNodes');

  activeNode = localStorage.getItem('activeNode');

  treeSettings = localStorage.getItem('treeSettings');

  dashboardPaneSizes = localStorage.getItem('dashboardPaneSizes');

  problemLogSettings = localStorage.getItem('problemLogSettings');

  mainMenuSettings = localStorage.getItem('mainMenuSettings');

  /*
  Мутация на сохранение настроек графика для ПМ в узлах
*/
  @Mutation
  saveDiagramSettingsMutation(payload: {
    settings: PMSettings;
    settingsName: 'userSettings' | 'tempSettings';
  }) {
    const value = window.localStorage.getItem(payload.settingsName);
    const settings = payload.settings;
    let modules: PMSettings[] = [];
    if (value) modules = JSON.parse(value);

    modules = modules.filter(
      (x: PMSettings) =>
        x.pModuleId !== settings.pModuleId ||
        x.measurementId !== settings.measurementId ||
        x.measurementType !== settings.measurementType,
    );
    modules.push(settings);
    window.localStorage.setItem(payload.settingsName, JSON.stringify(modules));
  }

  @Action
  getDiagramSettings(
    params: {
      pModuleId: any;
      measurementId: any;
      measurementType: MeasurementType;
    },
    settingsName?: 'userSettings' | 'tempSettings',
  ) {
    const value = window.localStorage.getItem(settingsName ?? 'userSettings');
    let modules: PMSettings[] = [];
    if (value) modules = JSON.parse(value);
    return modules.find(
      (x: PMSettings) =>
        x.pModuleId === params.pModuleId &&
        x.measurementId === params.measurementId &&
        x.measurementType === params.measurementType,
    );
  }

  @Action
  saveDiagramSettings(settings: PMSettings, settingsName?: 'userSettings' | 'tempSettings') {
    this.context.commit('saveDiagramSettingsMutation', {
      settings,
      settingsName: settingsName ?? 'userSettings',
    });
  }

  // Отдельное сохранение выбраных настроек графиков
  @Action
  getTempDiagramSettings(params: {
    pModuleId: any;
    measurementId: any;
    measurementType: MeasurementType;
  }) {
    return this.getDiagramSettings(params, 'tempSettings');
  }

  @Action
  saveTempDiagramSettings(settings: PMSettings) {
    this.saveDiagramSettings(settings, 'tempSettings');
  }

  // Вызываем удаление при завершении работы с настройками графиков(в диагностике и помощи)
  @Action
  clearTempDiagramSettings() {
    window.localStorage.removeItem('tempSettings');
  }

  /*
  Мутация на обновление координат виджета
*/
  @Mutation
  updateCoordinatesMutation(card: LayoutCard) {
    const value = window.localStorage.getItem('userDashboard');
    let cards: LayoutCard[] = [];
    if (value) cards = JSON.parse(value);
    cards = cards.filter((x: LayoutCard) => x.id !== card.id);
    cards.push(card);
    window.localStorage.setItem('userDashboard', JSON.stringify(cards));
  }

  /*
  Сохранение структуры предприятия в БД
*/
  @Action
  async saveFacStrMutation(strustureId: any) {
    try {
      window.localStorage.setItem('facilityStructure', JSON.stringify(strustureId));
      const {data} = await httpClient.get(`/profile`);
      data.facility_structure_settings = JSON.stringify(strustureId);
      await httpClient.put(`/profile`, data);
    } catch (e) {
      console.log(e);
      return;
    }
  }

  /*
Мутация на удаление виджетов/датчиков с дашборда
*/
  @Mutation
  removeCardsMutation(removeCards: LayoutCard[]) {
    const value = window.localStorage.getItem('userDashboard');

    if (!value) return;
    let cards = JSON.parse(value);
    cards = cards.filter((x: any) => !removeCards.some(c => x.id === c.id));
    window.localStorage.setItem('userDashboard', JSON.stringify(cards));
  }

  @Action
  async saveLocalSettingsToDB() {
    try {
      // сохраняем настройки из localStorage в БД
      const localStorageSettings = window.localStorage.getItem('userDashboard');
      const {data} = await httpClient.get(`/profile`);
      let cards: LayoutCard[] = [];
      const newCards: any[] = [];
      if (localStorageSettings) cards = JSON.parse(localStorageSettings);
      cards.forEach((card: any) => {
        if (card.type == SensorType.Sensor) {
          const newCard = {
            id: card.id,
            type: card.type,
            x: card.x,
            y: card.y,
            xl: card.xl,
            yl: card.yl,
            xm: card.xm,
            ym: card.ym,
            xs: card.xs,
            ys: card.ys,
            xss: card.xss,
            yss: card.yss,
            pmoduleId: card.pmoduleId,
            moduleType: card.moduleType,
            measurement: card.measurement,
          };
          newCards.push(JSON.parse(JSON.stringify(newCard)));
        } else {
          const newCard = {
            id: card.id,
            type: card.type,
            x: card.x,
            y: card.y,
            xl: card.xl,
            yl: card.yl,
            xm: card.xm,
            ym: card.ym,
            xs: card.xs,
            ys: card.ys,
            xss: card.xss,
            yss: card.yss,
            componentName: card.componentName,
            settings: card.settings,
            templateId: card.templateId,
          };
          newCards.push(JSON.parse(JSON.stringify(newCard)));
        }
      });
      data.dashboard_settings = JSON.stringify(newCards);
      await httpClient.put(`/profile`, data);
    } catch (e) {
      console.log(e);
      return;
    }
  }

  /*
  Действие на получение настроек виджетов
*/
  @Action
  async getCards() {
    try {
      const {data} = await httpClient.get(`/profile`);
      let cards: any[] = [];
      if (data.dashboard_settings) cards = JSON.parse(data.dashboard_settings);

      // обратная совместимость с числовыми типами модулей: если тип числовой, то используем текстовый
      cards.forEach((card: any, index, theArray) => {
        if (card.type == SensorType.Sensor) {
          if (!isNaN(card.moduleType)) {
            card.moduleType = Object.values(PModuleType)[card.moduleType - 1];
            theArray[index] = card;
          }
        }
      }, cards);

      window.localStorage.removeItem('userDashboard');
      window.localStorage.setItem('userDashboard', JSON.stringify(cards));
      return cards;
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  /*
  Действие на получение настроек структуры предприятия
*/
  @Action
  async getFacStr() {
    try {
      const {data} = await httpClient.get(`/profile`);
      let facStr: string[] = [];
      if (data.facility_structure_settings) facStr = JSON.parse(data.facility_structure_settings);
      window.localStorage.removeItem('facilityStructure');
      window.localStorage.setItem('facilityStructure', JSON.stringify(facStr));
      return facStr;
    } catch (e) {
      console.log(e);
      return [];
    }
  }

  /*
  Действие на изменение координат виджета/датчика
*/
  @Action
  updateCardCoordinates(card: LayoutCard) {
    this.context.commit('updateCoordinatesMutation', card);
  }

  /*
  Действие на удаление виджета/датчика с дашборда
*/
  @Action
  removeCards(cards: LayoutCard[]) {
    this.context.commit('removeCardsMutation', cards);
  }

  /*
  Мутация на сохранение выбранного измерения для ПМ в узлах
*/
  @Mutation
  saveSelectedMeasurementMutation(measurementSelection: PModelMeasurementSelection) {
    const value = window.localStorage.getItem('measurementSelection');
    let modules: PModelMeasurementSelection[] = [];
    if (value) modules = JSON.parse(value);

    modules = modules.filter(
      (x: PModelMeasurementSelection) => x.pModuleId !== measurementSelection.pModuleId,
    );
    modules.push(measurementSelection);
    window.localStorage.setItem('measurementSelection', JSON.stringify(modules));
  }

  // Действие на получение выбранного измерения для ПМ в узлах
  @Action
  getSelectedMeasurement(pModuleId: number) {
    const value = window.localStorage.getItem('measurementSelection');
    const modules: PModelMeasurementSelection[] = value ? JSON.parse(value) : [];
    return modules.find((x: PModelMeasurementSelection) => x.pModuleId === pModuleId);
  }

  @Action
  setTreeSettings(state: any) {
    this.context.commit('setTreeSettingsMutation', state);
  }

  // Сохранение открытых узлов дерева в локальном хранилище
  @Mutation
  setTreeSettingsMutation(state: string | null) {
    const treeSettings = JSON.stringify(state);
    this.treeSettings = treeSettings;
    if (state) localStorage.setItem('treeSettings', treeSettings);
    else localStorage.removeItem('treeSettings');
  }

  get getTreeSettings(): any {
    if (this.treeSettings) return JSON.parse(this.treeSettings);
    return [];
  }

  @Action
  setOpenNodes(state: any) {
    this.context.commit('setOpenNodesMutation', state);
  }

  // Сохранение открытых узлов дерева в локальном хранилище
  @Mutation
  setOpenNodesMutation(state: string | null) {
    const openNodes = JSON.stringify(state);
    this.openNodes = openNodes;
    if (state) localStorage.setItem('openNodes', openNodes);
    else localStorage.removeItem('openNodes');
  }

  get getOpenNodes(): any {
    if (this.openNodes) return JSON.parse(this.openNodes);
    return [];
  }

  @Action
  setActiveNode(state: SystemNode[]) {
    this.context.commit('setActiveNodeMutation', state);
  }

  // Сохранение выбранного узла в локальном хранилище
  @Mutation
  setActiveNodeMutation(state: string | null) {
    const activeNode = JSON.stringify(state);
    this.activeNode = activeNode;
    if (state) localStorage.setItem('activeNode', activeNode);
    else localStorage.removeItem('activeNode');
  }

  get getActiveNode(): SystemNode[] {
    if (this.activeNode) return JSON.parse(this.activeNode);
    return [];
  }

  @Action
  setDashboardPaneSizes(state: any) {
    this.context.commit('setDashboardPaneSizesMutation', state);
  }

  @Mutation
  setDashboardPaneSizesMutation(state: string | null) {
    const dashboardPaneSizes = JSON.stringify(state);
    this.dashboardPaneSizes = dashboardPaneSizes;
    if (state) localStorage.setItem('dashboardPaneSizes', dashboardPaneSizes);
    else localStorage.removeItem('dashboardPaneSizes');
  }

  get getDashboardPaneSizes(): any {
    if (this.dashboardPaneSizes) return JSON.parse(this.dashboardPaneSizes);
    return [];
  }

  @Action
  async setProblemLogSettings(state: any) {
    this.context.commit('setProblemLogSettingsMutation', state);
    const user = await userProfileModule.get();
    user.log_settings = JSON.stringify(state);
    await userProfileModule.save(user);
  }

  @Mutation
  setProblemLogSettingsMutation(state: string | null) {
    const problemLogSettings = JSON.stringify(state);
    this.problemLogSettings = problemLogSettings;
    if (state) localStorage.setItem('problemLogSettings', problemLogSettings);
    else localStorage.removeItem('problemLogSettings');
  }

  @Action
  async getLogSettings() {
    try {
      const user = await userProfileModule.get();
      let problemLogSettings: any = null;
      if (user.log_settings) problemLogSettings = JSON.parse(user.log_settings);
      this.context.commit('setProblemLogSettingsMutation', problemLogSettings);
      return problemLogSettings;
    } catch (e) {
      console.log(e);
      return null;
    }
  }

  @Action
  setMainMenuSettings(state: any) {
    this.context.commit('setMainMenuSettingsMutation', state);
  }

  @Mutation
  setMainMenuSettingsMutation(state: string | null) {
    const mainMenuSettings = JSON.stringify(state);
    this.mainMenuSettings = mainMenuSettings;
    if (state) localStorage.setItem('mainMenuSettings', mainMenuSettings);
    else localStorage.removeItem('mainMenuSettings');
  }
}

export default getModule(UserSettingsModule);
